import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BookOpen,
  TrendingUp,
  Calculator,
  BarChart3,
  Globe,
  Target,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowRight,
  Clock,
  GraduationCap,
  ChevronUp,
  ExternalLink,
  Share2
} from 'lucide-react';
import { PERCalculator, MarginOfSafetyCalculator, SimpleDCFCalculator } from '../components/Calculators';

const ValuationGuidePage = () => {
  // Scroll to section when hash changes
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.getElementById(hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="pt-20 pb-12 px-4 sm:px-6 lg:px-8 bg-[#0a0f1a]">
      <div className="max-w-4xl mx-auto">
        {/* Hero Header */}
        <div className="text-center mb-12">
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-4">
            <BookOpen className="w-3 h-3 mr-1" />
            Guide Complet
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Guide de la Valorisation Boursière
          </h1>
          <p className="text-lg text-gray-400 mb-6">
            Apprenez à évaluer les actions comme un professionnel
          </p>
          <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              15 min de lecture
            </span>
            <span className="flex items-center gap-1">
              <GraduationCap className="w-4 h-4" />
              Débutant à Intermédiaire
            </span>
          </div>
        </div>

        {/* Table of Contents */}
        <Card className="bg-white/5 border-white/10 mb-12">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-400" />
              Table des matières
            </h2>
            <nav className="grid sm:grid-cols-2 gap-2">
              {[
                { id: 'introduction', num: '1', title: "Qu'est-ce que la valorisation ?" },
                { id: 'multiples', num: '2', title: 'Les multiples de valorisation' },
                { id: 'dcf', num: '3', title: 'Le Discounted Cash Flow (DCF)' },
                { id: 'comparative', num: '4', title: "L'analyse comparative" },
                { id: 'emerging-markets', num: '5', title: 'Spécificités marchés émergents' },
                { id: 'practice', num: '6', title: 'Mettre en pratique' },
              ].map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors group"
                >
                  <span className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-sm font-medium group-hover:bg-emerald-500/30">
                    {item.num}
                  </span>
                  <span className="text-gray-300 group-hover:text-white transition-colors">
                    {item.title}
                  </span>
                </a>
              ))}
            </nav>
          </CardContent>
        </Card>

        {/* Section 1: Introduction */}
        <section id="introduction" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">1</span>
            <h2 className="text-2xl font-bold text-white">Introduction - Qu'est-ce que la valorisation ?</h2>
          </div>

          <div className="prose prose-invert max-w-none">
            <h3 className="text-xl font-semibold text-white mb-4">La question fondamentale</h3>
            <p className="text-gray-300 mb-4">
              Acheter une action, c'est acquérir une part d'entreprise. Mais <strong className="text-white">payez-vous le juste prix ?</strong>
            </p>
            <p className="text-gray-300 mb-6">
              La valorisation boursière répond à cette question en comparant :
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2 text-gray-300">
                <ArrowRight className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Le prix actuel</strong> (ce que demande le marché)</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <ArrowRight className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">La valeur intrinsèque</strong> (ce que vaut réellement l'entreprise)</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-4">Les trois scénarios</h3>
            <Card className="bg-gray-900/50 border-white/10 mb-6">
              <CardContent className="p-4 font-mono text-sm">
                <div className="text-emerald-400">Prix &lt; Valeur intrinsèque → <span className="text-white">Opportunité (sous-évalué)</span></div>
                <div className="text-gray-400">Prix = Valeur intrinsèque → <span className="text-white">Prix juste</span></div>
                <div className="text-red-400">Prix &gt; Valeur intrinsèque → <span className="text-white">Risque (surévalué)</span></div>
              </CardContent>
            </Card>

            <Card className="bg-emerald-500/10 border-emerald-500/30 mb-6">
              <CardContent className="p-4">
                <h4 className="font-semibold text-emerald-400 mb-2">Exemple concret</h4>
                <p className="text-gray-300 text-sm">
                  Une entreprise vietnamienne génère 10M USD de bénéfices annuels. Sa capitalisation boursière est de 80M USD.
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  <li className="text-gray-300"><strong className="text-white">PER = 80 / 10 = 8</strong> (elle vaut 8 années de bénéfices)</li>
                  <li className="text-gray-300">Si le PER moyen du secteur est 15 → <strong className="text-emerald-400">Elle est potentiellement sous-évaluée</strong></li>
                </ul>
              </CardContent>
            </Card>

            <h3 className="text-xl font-semibold text-white mb-4">Pourquoi c'est important</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <Card className="bg-red-500/10 border-red-500/30">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Sans valorisation
                  </h4>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>Vous payez trop cher une entreprise médiocre</li>
                    <li>Vous ratez des pépites décotées</li>
                    <li>Vous subissez la volatilité émotionnelle</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="bg-emerald-500/10 border-emerald-500/30">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Avec valorisation
                  </h4>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>Vous identifiez les vraies opportunités</li>
                    <li>Vous construisez une marge de sécurité</li>
                    <li>Vous dormez tranquille (discipline &gt; émotion)</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Section 2: Multiples */}
        <section id="multiples" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">2</span>
            <h2 className="text-2xl font-bold text-white">Les multiples de valorisation</h2>
          </div>

          {/* PER Section */}
          <div className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-emerald-400" />
              Le PER (Price/Earnings Ratio)
            </h3>

            <Card className="bg-gray-900/50 border-white/10 mb-4">
              <CardContent className="p-4">
                <p className="text-gray-400 text-sm mb-2">Formule :</p>
                <code className="text-emerald-400 text-lg">PER = Capitalisation boursière / Bénéfice net</code>
              </CardContent>
            </Card>

            <p className="text-gray-300 mb-4">
              Un PER de 12 signifie que vous payez <strong className="text-white">12 années de bénéfices</strong> pour posséder l'entreprise.
            </p>

            <h4 className="font-semibold text-white mb-3">Règles empiriques :</h4>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <Card className="bg-emerald-500/10 border-emerald-500/30">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-400">&lt; 10</div>
                  <div className="text-xs text-gray-400">Potentiellement sous-évalué</div>
                </CardContent>
              </Card>
              <Card className="bg-yellow-500/10 border-yellow-500/30">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-400">10-20</div>
                  <div className="text-xs text-gray-400">Valorisation normale</div>
                </CardContent>
              </Card>
              <Card className="bg-red-500/10 border-red-500/30">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-red-400">&gt; 25</div>
                  <div className="text-xs text-gray-400">Cher (ou forte croissance)</div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-amber-500/10 border-amber-500/30 mb-6">
              <CardContent className="p-4">
                <h4 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Attention aux pièges
                </h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-white font-medium">Piège 1 : Bénéfices exceptionnels</p>
                    <p className="text-gray-400">Entreprise vend un actif → Bénéfice ponctuel → PER artificiellement bas</p>
                  </div>
                  <div>
                    <p className="text-white font-medium">Piège 2 : Secteurs différents</p>
                    <p className="text-gray-400">PER Tech (25-40) ≠ PER Banque (8-12). Comparer uniquement dans le même secteur.</p>
                  </div>
                  <div>
                    <p className="text-white font-medium">Piège 3 : Qualité comptable douteuse</p>
                    <p className="text-gray-400">Bénéfices manipulés (creative accounting). Vérifier avec le Free Cash Flow.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* P/B Section */}
          <div className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
              Le P/B (Price/Book Ratio)
            </h3>

            <Card className="bg-gray-900/50 border-white/10 mb-4">
              <CardContent className="p-4">
                <p className="text-gray-400 text-sm mb-2">Formule :</p>
                <code className="text-emerald-400 text-lg">P/B = Capitalisation boursière / Capitaux propres</code>
              </CardContent>
            </Card>

            <p className="text-gray-300 mb-4">
              Un P/B de 1.5 signifie que vous payez <strong className="text-white">1.5x</strong> la valeur des actifs nets de l'entreprise.
            </p>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <Card className="bg-emerald-500/10 border-emerald-500/30">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-400">&lt; 1</div>
                  <div className="text-xs text-gray-400">Value trap ou opportunité</div>
                </CardContent>
              </Card>
              <Card className="bg-yellow-500/10 border-yellow-500/30">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-400">1-3</div>
                  <div className="text-xs text-gray-400">Valorisation normale</div>
                </CardContent>
              </Card>
              <Card className="bg-red-500/10 border-red-500/30">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-red-400">&gt; 5</div>
                  <div className="text-xs text-gray-400">Actifs intangibles</div>
                </CardContent>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <Card className="bg-emerald-500/10 border-emerald-500/30">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Pertinent pour
                  </h4>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>Banques (actifs = prêts, dépôts)</li>
                    <li>Immobilier (actifs tangibles)</li>
                    <li>Industries lourdes (usines, terrains)</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="bg-gray-500/10 border-gray-500/30">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-gray-400 mb-2 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Moins pertinent pour
                  </h4>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>Tech (actifs immatériels non comptabilisés)</li>
                    <li>Services (peu d'actifs physiques)</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* EV/EBITDA Section */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Le EV/EBITDA
            </h3>

            <Card className="bg-gray-900/50 border-white/10 mb-4">
              <CardContent className="p-4">
                <p className="text-gray-400 text-sm mb-2">Formule :</p>
                <code className="text-emerald-400">EV = Capitalisation + Dette - Cash</code>
                <br />
                <code className="text-emerald-400">EV/EBITDA = Valeur d'entreprise / EBITDA</code>
              </CardContent>
            </Card>

            <p className="text-gray-300 mb-4">
              Prend en compte <strong className="text-white">la dette</strong> → Vision plus complète qu'un simple PER
            </p>

            <Card className="bg-blue-500/10 border-blue-500/30 mb-6">
              <CardContent className="p-4">
                <h4 className="font-semibold text-blue-400 mb-2">Exemple révélateur</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-white">Entreprise A : PER 10, dette 0</p>
                    <p className="text-emerald-400">→ Risque faible</p>
                  </div>
                  <div>
                    <p className="text-white">Entreprise B : PER 10, dette = 2x capitalisation</p>
                    <p className="text-red-400">→ Risque élevé !</p>
                  </div>
                </div>
                <p className="text-gray-400 mt-2 text-sm">EV/EBITDA révèle cette différence cruciale.</p>
              </CardContent>
            </Card>
          </div>

          {/* Interactive PER Calculator */}
          <div className="my-8">
            <PERCalculator />
          </div>

          {/* CTA after multiples */}
          <Card className="bg-gradient-to-r from-emerald-500/20 to-blue-500/20 border-emerald-500/30">
            <CardContent className="p-6 text-center">
              <p className="text-gray-300 mb-4">Filtrez les actions par Real P/E ajusté pour l'inflation</p>
              <Link to="/screener">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  Accéder au Screener
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>

        {/* Section 3: DCF */}
        <section id="dcf" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">3</span>
            <h2 className="text-2xl font-bold text-white">Le Discounted Cash Flow (DCF)</h2>
          </div>

          <h3 className="text-xl font-semibold text-white mb-4">Principe fondamental</h3>
          <Card className="bg-blue-500/10 border-blue-500/30 mb-6">
            <CardContent className="p-4">
              <p className="text-blue-300 font-medium text-lg">
                Une entreprise vaut la somme de tous ses flux de trésorerie futurs, actualisés à aujourd'hui
              </p>
            </CardContent>
          </Card>

          <p className="text-gray-300 mb-6">
            C'est la méthode "académique" utilisée par les analystes professionnels.
          </p>

          <h3 className="text-xl font-semibold text-white mb-4">La formule (simplifiée)</h3>
          <Card className="bg-gray-900/50 border-white/10 mb-6">
            <CardContent className="p-4 font-mono">
              <code className="text-emerald-400">Valeur = Σ (Free Cash Flow année N) / (1 + taux d'actualisation)^N</code>
            </CardContent>
          </Card>

          <p className="text-gray-300 mb-4">En pratique :</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-300 mb-6">
            <li>Estimer les cash flows futurs (5-10 ans)</li>
            <li>Les ramener à aujourd'hui (actualisation)</li>
            <li>Ajouter une valeur terminale (après 10 ans)</li>
          </ol>

          <h3 className="text-xl font-semibold text-white mb-4">Exemple concret</h3>
          <Card className="bg-gray-900/50 border-white/10 mb-6 overflow-x-auto">
            <CardContent className="p-4">
              <p className="text-gray-400 text-sm mb-3">
                <strong className="text-white">Entreprise :</strong> Tech vietnamienne, FCF actuel = 10M USD
              </p>
              <p className="text-gray-400 text-sm mb-4">
                Croissance : 15%/an | Taux actualisation : 10% | Valeur terminale : 20x FCF année 5
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 text-gray-400">Année</th>
                    <th className="text-right py-2 text-gray-400">FCF</th>
                    <th className="text-right py-2 text-gray-400">FCF actualisé</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr><td className="py-1">1</td><td className="text-right">11.5M</td><td className="text-right">10.5M</td></tr>
                  <tr><td className="py-1">2</td><td className="text-right">13.2M</td><td className="text-right">10.9M</td></tr>
                  <tr><td className="py-1">3</td><td className="text-right">15.2M</td><td className="text-right">11.4M</td></tr>
                  <tr><td className="py-1">4</td><td className="text-right">17.5M</td><td className="text-right">12.0M</td></tr>
                  <tr><td className="py-1">5</td><td className="text-right">20.1M</td><td className="text-right">12.5M</td></tr>
                </tbody>
                <tfoot className="border-t border-white/10">
                  <tr><td colSpan="2" className="py-2 text-gray-400">FCF cumulé actualisé</td><td className="text-right text-white">57M USD</td></tr>
                  <tr><td colSpan="2" className="py-1 text-gray-400">Valeur terminale actualisée</td><td className="text-right text-white">249M USD</td></tr>
                  <tr className="bg-emerald-500/10"><td colSpan="2" className="py-2 font-bold text-emerald-400">Valeur intrinsèque totale</td><td className="text-right font-bold text-emerald-400">306M USD</td></tr>
                </tfoot>
              </table>
              <p className="mt-4 text-emerald-400 font-medium">
                Si capitalisation actuelle = 200M USD → <strong>53% de potentiel de hausse</strong>
              </p>
            </CardContent>
          </Card>

          <Card className="bg-amber-500/10 border-amber-500/30 mb-6">
            <CardContent className="p-4">
              <h4 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Limites du DCF
              </h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Très sensible aux hypothèses</strong> - Changer croissance de 15% → 12% = -30% de valeur</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Difficile pour entreprises cycliques</strong> - Cash flows volatils = estimations hasardeuses</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Inadapté pour startups</strong> - Pas de cash flow historique fiable</span>
                </li>
              </ul>
              <p className="mt-3 text-amber-400 text-sm">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                Utilisez le DCF comme <strong>confirmation</strong>, pas comme vérité absolue
              </p>
            </CardContent>
          </Card>

          {/* Interactive DCF Calculator */}
          <div className="my-8">
            <SimpleDCFCalculator />
          </div>
        </section>

        {/* Section 4: Comparative Analysis */}
        <section id="comparative" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">4</span>
            <h2 className="text-2xl font-bold text-white">L'analyse comparative</h2>
          </div>

          <h3 className="text-xl font-semibold text-white mb-4">Principe</h3>
          <p className="text-gray-300 mb-6">
            <strong className="text-white">Comparer avec des entreprises similaires pour détecter les anomalies</strong>
          </p>
          <p className="text-gray-300 mb-6">
            Si 4 banques polonaises ont un PER de 8, et la 5ème un PER de 4 → <strong className="text-emerald-400">Pourquoi cette différence ?</strong>
          </p>

          <h3 className="text-xl font-semibold text-white mb-4">Comment comparer efficacement</h3>
          
          <h4 className="font-semibold text-white mb-3">1. Définir le peer group (groupe de pairs)</h4>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="p-4">
                <h5 className="font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Bon peer group
                </h5>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>Même pays/région</li>
                  <li>Même secteur</li>
                  <li>Taille comparable</li>
                  <li>Modèle économique similaire</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-4">
                <h5 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Mauvais peer group
                </h5>
                <p className="text-sm text-gray-300">
                  Comparer Samsung (conglomérat) avec une startup fintech
                </p>
              </CardContent>
            </Card>
          </div>

          <h4 className="font-semibold text-white mb-3">2. Utiliser plusieurs multiples</h4>
          <Card className="bg-gray-900/50 border-white/10 mb-6 overflow-x-auto">
            <CardContent className="p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 text-gray-400">Entreprise</th>
                    <th className="text-right py-2 text-gray-400">PER</th>
                    <th className="text-right py-2 text-gray-400">P/B</th>
                    <th className="text-right py-2 text-gray-400">EV/EBITDA</th>
                    <th className="text-right py-2 text-gray-400">Div. Yield</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr><td className="py-1">Peer 1</td><td className="text-right">12</td><td className="text-right">1.8</td><td className="text-right">9</td><td className="text-right">4.2%</td></tr>
                  <tr><td className="py-1">Peer 2</td><td className="text-right">11</td><td className="text-right">1.6</td><td className="text-right">8</td><td className="text-right">4.5%</td></tr>
                  <tr><td className="py-1">Peer 3</td><td className="text-right">13</td><td className="text-right">2.0</td><td className="text-right">10</td><td className="text-right">3.8%</td></tr>
                  <tr className="bg-emerald-500/10 font-bold text-emerald-400">
                    <td className="py-1">Votre cible</td><td className="text-right">8</td><td className="text-right">1.2</td><td className="text-right">6</td><td className="text-right">5.5%</td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-3 text-emerald-400 font-medium">
                → Cible décotée sur TOUS les critères = Signal fort
              </p>
            </CardContent>
          </Card>

          <h4 className="font-semibold text-white mb-3">3. Investiguer les différences</h4>
          <p className="text-gray-300 mb-4">Pourquoi la cible est moins chère ?</p>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="p-4">
                <h5 className="font-semibold text-emerald-400 mb-2">Bonnes raisons</h5>
                <p className="text-sm text-gray-300">Temporairement impopulaire, sentiment négatif excessif</p>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-4">
                <h5 className="font-semibold text-red-400 mb-2">Mauvaises raisons</h5>
                <p className="text-sm text-gray-300">Dette cachée, management faible, procès en cours</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Section 5: Emerging Markets */}
        <section id="emerging-markets" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">5</span>
            <h2 className="text-2xl font-bold text-white">Spécificités des marchés émergents</h2>
          </div>

          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-400" />
            Pourquoi valoriser différemment ?
          </h3>

          <p className="text-gray-300 mb-6">
            Les marchés émergents (Vietnam, Inde, Pologne, Philippines, Indonésie, Corée) ont des <strong className="text-white">primes de risque</strong> spécifiques.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-4">
                <h4 className="font-semibold text-red-400 mb-3">Risques additionnels</h4>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>Volatilité politique</li>
                  <li>Stabilité monétaire (dévaluation)</li>
                  <li>Qualité comptable variable</li>
                  <li>Liquidité moindre</li>
                  <li>Risques réglementaires</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="p-4">
                <h4 className="font-semibold text-emerald-400 mb-3">Opportunités compensatrices</h4>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>Croissance économique supérieure (5-7% vs 2%)</li>
                  <li>Valorisations décotées (PER 8-12 vs 20-25 US)</li>
                  <li>Inefficiences de marché (moins d'analystes)</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <h3 className="text-xl font-semibold text-white mb-4">Ajuster les multiples</h3>
          <Card className="bg-blue-500/10 border-blue-500/30 mb-6">
            <CardContent className="p-4">
              <p className="text-gray-400 text-sm mb-2">Règle empirique :</p>
              <code className="text-blue-400 block mb-3">PER acceptable = PER marché développé × (1 - Prime de risque pays)</code>
              <div className="bg-gray-900/50 p-3 rounded-lg text-sm">
                <p className="text-gray-300"><strong className="text-white">Exemple :</strong></p>
                <p className="text-gray-300">PER acceptable US : 18</p>
                <p className="text-gray-300">Prime risque Vietnam : 30%</p>
                <p className="text-emerald-400 font-medium">PER acceptable Vietnam : 18 × 0.7 = 12.6</p>
                <p className="text-gray-400 mt-2">→ Un PER de 10 au Vietnam = équivalent PER 14 aux US</p>
              </div>
            </CardContent>
          </Card>

          <h3 className="text-xl font-semibold text-white mb-4">Décote illiquidité</h3>
          <Card className="bg-gray-900/50 border-white/10 mb-6 overflow-x-auto">
            <CardContent className="p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 text-gray-400">Volume quotidien</th>
                    <th className="text-right py-2 text-gray-400">Décote appliquée</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr><td className="py-2">&lt; 100K USD</td><td className="text-right text-red-400">-20% à -30%</td></tr>
                  <tr><td className="py-2">100K - 1M USD</td><td className="text-right text-yellow-400">-10% à -15%</td></tr>
                  <tr><td className="py-2">&gt; 1M USD</td><td className="text-right text-emerald-400">-5% à -10%</td></tr>
                </tbody>
              </table>
            </CardContent>
          </Card>

          <h3 className="text-xl font-semibold text-white mb-4">Standards comptables par pays</h3>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="p-3 text-center">
                <div className="text-emerald-400 font-bold mb-1">Corée, Pologne</div>
                <div className="text-xs text-gray-400">IFRS strict, audits fiables</div>
              </CardContent>
            </Card>
            <Card className="bg-yellow-500/10 border-yellow-500/30">
              <CardContent className="p-3 text-center">
                <div className="text-yellow-400 font-bold mb-1">Inde, Indonésie</div>
                <div className="text-xs text-gray-400">IFRS, application variable</div>
              </CardContent>
            </Card>
            <Card className="bg-orange-500/10 border-orange-500/30">
              <CardContent className="p-3 text-center">
                <div className="text-orange-400 font-bold mb-1">Vietnam, Philippines</div>
                <div className="text-xs text-gray-400">Standards locaux, vigilance</div>
              </CardContent>
            </Card>
          </div>

          {/* CTA after emerging markets */}
          <Card className="bg-gradient-to-r from-emerald-500/20 to-blue-500/20 border-emerald-500/30">
            <CardContent className="p-6 text-center">
              <p className="text-gray-300 mb-4">Découvrez les tendances macro temps réel pour chaque pays</p>
              <Link to="/markets">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  Voir les Marchés
                  <Globe className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>

        {/* Section 6: Practice */}
        <section id="practice" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">6</span>
            <h2 className="text-2xl font-bold text-white">Mettre en pratique avec VALORAMAP</h2>
          </div>

          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-emerald-400" />
            Étape 1 : Filtrer avec le Screener
          </h3>

          <Card className="bg-emerald-500/10 border-emerald-500/30 mb-6">
            <CardContent className="p-4">
              <p className="text-gray-300 mb-3"><strong className="text-white">Scénario :</strong> Trouver des actions value décotées au Vietnam</p>
              <ol className="space-y-2 text-sm text-gray-300">
                <li>1. Accéder au <Link to="/screener" className="text-emerald-400 hover:underline">Screener VALORAMAP</Link></li>
                <li>2. Filtrer :
                  <ul className="ml-4 mt-1 space-y-1">
                    <li>• <strong className="text-white">Pays :</strong> Vietnam</li>
                    <li>• <strong className="text-white">Real P/E :</strong> &lt; 12 (ajusté inflation)</li>
                    <li>• <strong className="text-white">P/B :</strong> &lt; 2</li>
                    <li>• <strong className="text-white">ROE :</strong> &gt; 15%</li>
                    <li>• <strong className="text-white">Score Liquidité :</strong> &gt; 60</li>
                  </ul>
                </li>
                <li>3. Trier par <strong className="text-white">Real P/E croissant</strong></li>
              </ol>
            </CardContent>
          </Card>

          <h3 className="text-xl font-semibold text-white mb-4">Étape 2 : Checklist valorisation</h3>
          <Card className="bg-gray-900/50 border-white/10 mb-6">
            <CardContent className="p-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-emerald-400 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Critères de sélection
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li className="flex items-center gap-2"><div className="w-4 h-4 border border-white/30 rounded"></div>Real P/E &lt; Moyenne secteur</li>
                    <li className="flex items-center gap-2"><div className="w-4 h-4 border border-white/30 rounded"></div>P/B &lt; 2</li>
                    <li className="flex items-center gap-2"><div className="w-4 h-4 border border-white/30 rounded"></div>Free Cash Flow positif</li>
                    <li className="flex items-center gap-2"><div className="w-4 h-4 border border-white/30 rounded"></div>Dette/Capitaux propres &lt; 1</li>
                    <li className="flex items-center gap-2"><div className="w-4 h-4 border border-white/30 rounded"></div>ROE &gt; 15%</li>
                    <li className="flex items-center gap-2"><div className="w-4 h-4 border border-white/30 rounded"></div>Score Qualité Comptable &gt; 50</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-red-400 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Red Flags (à éviter)
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-400" />Bénéfices volatils (-50% puis +200%)</li>
                    <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-400" />Dette explosive (doublement en 2 ans)</li>
                    <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-400" />Cash flow négatif récurrent</li>
                    <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-400" />Beneish M-Score &gt; -2.22</li>
                    <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-400" />Altman Z''-Score &lt; 1.1</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <h3 className="text-xl font-semibold text-white mb-4">Étape 3 : Construire votre marge de sécurité</h3>
          <Card className="bg-blue-500/10 border-blue-500/30 mb-6">
            <CardContent className="p-4">
              <blockquote className="text-blue-300 italic mb-3 border-l-4 border-blue-400 pl-4">
                "Le prix est ce que vous payez, la valeur est ce que vous obtenez"
                <span className="block text-sm text-blue-400 mt-1">— Benjamin Graham</span>
              </blockquote>
              <div className="bg-gray-900/50 p-3 rounded-lg">
                <p className="text-gray-400 text-sm mb-2">Règle des 40% :</p>
                <code className="text-blue-400 block mb-2">Prix d'achat max = Valeur intrinsèque × 0.6</code>
                <p className="text-gray-300 text-sm">
                  <strong className="text-white">Exemple :</strong> Valeur DCF = 100 USD → Prix max = 60 USD → Marge de sécurité = 40%
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Interactive Margin of Safety Calculator */}
          <div className="my-8">
            <MarginOfSafetyCalculator />
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-16">
          <Card className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border-emerald-500/30">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <Target className="w-6 h-6 text-emerald-400" />
                Conclusion : Valorisation = Discipline
              </h2>

              <h3 className="text-lg font-semibold text-white mb-4">Les 5 commandements de la valorisation</h3>
              <ol className="space-y-2 mb-8">
                {[
                  'Jamais acheter sans valoriser (prix ≠ valeur)',
                  'Utiliser plusieurs méthodes (PER + P/B + DCF)',
                  'Comparer avec peers (contexte sectoriel)',
                  'Ajuster pour marchés émergents (prime de risque)',
                  'Garder une marge de sécurité (40% minimum)'
                ].map((cmd, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <span className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-sm">{i + 1}</span>
                    {cmd}
                  </li>
                ))}
              </ol>

              <h3 className="text-lg font-semibold text-white mb-4">Votre avantage sur marchés émergents</h3>
              <p className="text-gray-300 mb-4">
                Les investisseurs institutionnels boudent ces marchés car : trop petits, perçus comme risqués, mal couverts par analystes.
                <strong className="text-emerald-400"> = Opportunités pour vous !</strong>
              </p>

              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {[
                  'Données macroéconomiques temps réel',
                  'Métriques ajustées inflation (Real P/E, Real ROE)',
                  'Scores qualité comptable (Beneish, Altman, Piotroski)',
                  'Screener avancé avec filtres',
                  'Scores de liquidité (éviter pièges)'
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    {feature}
                  </div>
                ))}
              </div>

              <p className="text-lg text-white font-medium text-center mb-6">
                Vous investissez comme un professionnel, avec les outils des pros.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/screener">
                  <Button size="lg" className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white">
                    Accéder au Screener
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10">
                    Voir les tarifs
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Resources */}
        <section className="mb-16">
          <h2 className="text-xl font-bold text-white mb-6">Ressources complémentaires</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <h3 className="font-semibold text-white mb-3">Lectures recommandées</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li><strong className="text-emerald-400">L'Investisseur Intelligent</strong> - Benjamin Graham</li>
                  <li><strong className="text-emerald-400">Security Analysis</strong> - Graham & Dodd</li>
                  <li><strong className="text-emerald-400">Margin of Safety</strong> - Seth Klarman</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <h3 className="font-semibold text-white mb-3">Outils VALORAMAP</h3>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/screener" className="text-emerald-400 hover:underline">Screener avancé</Link> - Filtrer par Real P/E, qualité comptable</li>
                  <li><Link to="/markets" className="text-emerald-400 hover:underline">Tendances Macro</Link> - Contexte économique par pays</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>Guide rédigé par l'équipe VALORAMAP</p>
          <p>Dernière mise à jour : Février 2026 · Temps de lecture : 15 minutes</p>
        </div>

        {/* Scroll to top button */}
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg transition-all z-50"
          aria-label="Retour en haut"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default ValuationGuidePage;
